// client/src/components/unidades/UnidadeManagementDrawer.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUnidade } from '../../hooks/useUnidades';
import { useAuth } from '../../context/auth/AuthProvider';
import {
    ArrowLeft,
    Loader2, AlertCircle, XCircle
} from 'lucide-react';
import useAlert from '../../hooks/useAlert';
import { FormStepper } from '../shared/FormStepper';

// Importação das seções (Tabs)
import UnidadeProfileSection from './sections/UnidadeProfileSection';
import UnidadeProfissionaisSection from './sections/UnidadeProfissionaisSection';
import UnidadeConfigTab from './tabs/UnidadeConfigTab';
import UnidadeImagensSection from './sections/UnidadeImagensSection';
import HorarioAtendimentoTab from './tabs/HorarioAtendimentoTab'; 

const safeId = (data) => {
    if (!data) return '';
    if (typeof data === 'string') return data;
    if (data._id) return String(data._id);
    if (data.id) return String(data.id);
    return '';
};

// GERAR INICIAIS PARA PLACEHOLDER
const getIniciais = (nome) => {
    if (!nome) return 'UN';
    return nome
        .split(' ')
        .map(word => word[0])
        .slice(0, 2)
        .join('')
        .toUpperCase();
};

const UnidadeManagementDrawer = ({ contextData }) => {
    const { unidadeId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { setAlert } = useAlert();
    const { user } = useAuth();
    
    const [activeTab, setActiveTab] = useState('unidade');

    // VALIDAR unidadeId ANTES DE QUALQUER COISA
    useEffect(() => {
        if (!unidadeId || unidadeId === 'undefined' || unidadeId === 'null') {
            console.error('❌ ID da unidade inválido:', unidadeId);
            setAlert('ID da unidade inválido', 'danger');
            navigate('/minha-conta/unidades');
        }
    }, [unidadeId, navigate, setAlert]);

    // Capturar a tab da URL
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        if (tab && ['unidade', 'profissionais', 'horarios', 'imagens', 'configuracoes'].includes(tab)) {
            setActiveTab(tab);
        }
    }, [location.search]);

    // OBTER CONTA ID DE FORMA CONFIÁVEL
    const targetAccountId = useMemo(() => {
        if (contextData?.contaId) return safeId(contextData.contaId);
        if (location.state?.contaId) return safeId(location.state.contaId);
        if (user) return safeId(user.account?._id || user.account);
        return null;
    }, [contextData?.contaId, location.state?.contaId, user]);

    // Só executar a query se unidadeId for válido
    const { 
        data: unidadeData, 
        isLoading, 
        error,
        refetch: refetchUnidade
    } = useUnidade(unidadeId, {
        enabled: !!unidadeId && unidadeId !== 'undefined' && unidadeId !== 'null'
    });

    const handleRefresh = useCallback(() => {
        refetchUnidade();
    }, [refetchUnidade]);

    const handleUpdateSuccess = useCallback(() => {
        refetchUnidade();
    }, [refetchUnidade]);

    if (!unidadeId || unidadeId === 'undefined' || unidadeId === 'null') {
        return null; 
    }

    if (isLoading) {
        return (
            <div className="page-management-container">
                <div className="table-loading-state py-20 flex flex-col items-center">
                    <Loader2 size={32} className="animate-spin text-primary mb-4" />
                    <span className="text-gray-500 font-medium">Carregando dados da unidade...</span>
                </div>
            </div>
        );
    }

    if (error || !unidadeData?.data) {
        return (
            <div className="page-management-container">
                <div className="content-panel">
                    <div className="text-center py-12">
                        <AlertCircle className="text-red-500 mb-4 mx-auto" size={48} />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {error?.message || 'Unidade não encontrada'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Não foi possível carregar os dados da unidade.
                            {error?.response?.status === 400 && ' ID inválido.'}
                        </p>
                        <button
                            onClick={() => navigate('/minha-conta/unidades')}
                            className="action-button action-button-secondary"
                        >
                            <ArrowLeft size={16} className="icon" /> Voltar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const unidade = unidadeData.data;
    const iniciais = getIniciais(unidade.nome);

    // Configuração das Abas para o cabeçalho de passos
    const tabsList = [
        { id: 'unidade', label: 'Unidade' },
        { id: 'profissionais', label: 'Profissionais' },
        { id: 'horarios', label: 'Horários' },
        { id: 'imagens', label: 'Imagens' },
        { id: 'configuracoes', label: 'Configurações' }
    ];
    const stepLabels = tabsList.map(tab => tab.label);
    const currentStep = Math.max(1, tabsList.findIndex(tab => tab.id === activeTab) + 1);
    const goToTab = (stepNumber) => {
        const tab = tabsList[stepNumber - 1];
        if (!tab) return;
        setActiveTab(tab.id);
        navigate(`/minha-conta/unidades/${unidadeId}/gerenciar?tab=${tab.id}`, { replace: true });
    };

    // RENDERIZAÇÃO CONDICIONAL DAS ABAS
    const renderSectionContent = () => {
        switch (activeTab) {
            case 'unidade':
                return (
                    <UnidadeProfileSection 
                        unidade={unidade}
                        contextData={{ contaId: targetAccountId }}
                        onUpdateSuccess={handleUpdateSuccess}
                    />
                );
            case 'profissionais':
                return (
                    <UnidadeProfissionaisSection 
                        unidade={unidade} 
                        contextData={{ contaId: targetAccountId }}
                    />
                );
            case 'horarios': 
                return (
                    <HorarioAtendimentoTab 
                        unidade={unidade}
                        isCreating={false}
                    />
                );
            case 'imagens':
                return (
                    <UnidadeImagensSection
                        unidade={unidade}
                        onUpdateSuccess={handleUpdateSuccess}
                    />
                );
            case 'configuracoes':
                return (
                    <UnidadeConfigTab
                        unidade={unidade}
                        onUpdate={handleRefresh}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div className="page-management-container">
            <div className="page-header border-b pb-4 mb-4">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between w-full gap-4">
                    <div className="flex items-start md:items-center gap-4 flex-1 min-w-0">
                        <button
                            onClick={() => navigate('/minha-conta/unidades')}
                            className="action-button action-button-secondary text-sm shrink-0 mt-1 md:mt-0"
                        >
                            <ArrowLeft size={16} className="icon" />
                            Voltar
                        </button>
                        
                        <div className="flex items-center gap-3 min-w-0 flex-1">
                            <div className="relative shrink-0">
                                <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-br from-primary-100 to-primary-200 border-2 border-white shadow-sm flex items-center justify-center text-primary-700 font-bold text-lg">
                                    {unidade.imagemUrl ? (
                                        <img 
                                            src={unidade.imagemUrl} 
                                            alt={unidade.nome}
                                            className="w-full h-full object-cover"
                                            onError={(e) => {
                                                e.target.onerror = null;
                                                e.target.style.display = 'none';
                                                e.target.parentNode.innerHTML = `<span class="text-primary-700 font-bold text-lg">${iniciais}</span>`;
                                            }}
                                        />
                                    ) : (
                                        <span className="text-primary-700 font-bold text-lg">{iniciais}</span>
                                    )}
                                </div>
                            </div>

                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-1">
                                    <h1 className="page-title mb-0 text-xl font-bold text-gray-800 truncate max-w-full" title={unidade.nome}>
                                        {unidade.nome}
                                    </h1>
                                    <span className="inline-flex shrink-0 items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                                        Unidade
                                    </span>
                                </div>
                                <div className="text-sm text-gray-500 truncate">
                                    {unidade.endereco?.localidade}/{unidade.endereco?.uf}
                                    {unidade.codigo && ` • Código: ${unidade.codigo}`}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* NAVEGAÇÃO DE ABAS */}
            <FormStepper currentStep={currentStep} steps={stepLabels} onStepClick={goToTab} />

            {/* CONTEÚDO DA ABA SELECIONADA E BOTÕES DE AÇÃO NA BASE (RODAPÉ) */}
            <div className="content-panel bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 min-h-[400px]">
                    {renderSectionContent()}
                </div>

                <div className="form-actions mt-0 p-6 border-t bg-gray-50 flex justify-between rounded-b-lg">
                    <button type="button" onClick={() => navigate('/minha-conta/unidades')} className="action-button action-button-secondary">
                        <XCircle size={18} className="icon" /> Fechar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnidadeManagementDrawer;